import type {
  AccessoryProductType,
  AccessoryWithQuantityType,
  SelectedSystemType
} from 'data/types';
import type {
  QuotableSystemAccessoryRecommendationType,
  RequiredAccessoryGroupType
} from 'features/api/accessories-api-types';
import { collectSingleChoiceAlternativesToClear } from 'utils/accessory-option-groups';
import {
  buildSavedAccessoryProductLookup,
  findRecommendedLineSetProductId,
  replaceLineSet,
  selectedQuantityOf,
  setAccessoryQuantity
} from 'utils/ducted-accessories';
import type {
  DuctedAccessoryPanelDataType,
  DuctedAccessorySavePayloadItemType,
  DuctedAccessorySelectionChangeType,
  DuctedAccessorySelectionsType,
  DuctedLineSetScopeType,
  DuctedRecommendedSelectionType,
  DuctedSavedAccessoryRowType
} from 'utils/ducted-accessories-types';
import { collectCartableRecommendedAccessories } from 'utils/include-recommended-accessories';
import type { ManualAccessoryEntryType } from 'utils/manual-accessories-types';
import { combineProductQuantities } from 'utils/products';

const UNIT_PRODUCT_TYPE = 'condenser';
const NO_INCLUDED_ACCESSORY_TYPES = new Map<string, Set<string>>();

type ReadSelectionsParams = {
  system: SelectedSystemType;
  recommendations: QuotableSystemAccessoryRecommendationType[];
};

type HydrateSelectionsParams = ReadSelectionsParams & {
  savedRows: DuctedSavedAccessoryRowType[];
  requiredGroups: RequiredAccessoryGroupType[];
};

type SavedRowTargetType = {
  equipmentUniqueId: string;
  quantity: number;
};

const isUnitScopedRecommendation = (
  rec: QuotableSystemAccessoryRecommendationType
): boolean => rec.productType === UNIT_PRODUCT_TYPE;

const unitScopedProductIds = (
  recommendations: QuotableSystemAccessoryRecommendationType[],
  unitUniqueId?: string
): Set<number> =>
  new Set(
    recommendations
      .filter(
        (rec) =>
          isUnitScopedRecommendation(rec) &&
          (unitUniqueId === undefined || rec.uniqueId === unitUniqueId)
      )
      .map((rec) => rec.accessoryProductId)
  );

const emptyDuctedAccessorySelections = (
  system: SelectedSystemType
): DuctedAccessorySelectionsType => ({
  systemUniqueId: system.uniqueId,
  system: [],
  units: Object.fromEntries(
    system.configuration.condensers.map((condenser) => [condenser.uniqueId, []])
  )
});

export function readDuctedAccessorySelections({
  system,
  recommendations
}: ReadSelectionsParams): DuctedAccessorySelectionsType {
  const allUnitProductIds = unitScopedProductIds(recommendations);
  return {
    systemUniqueId: system.uniqueId,
    system: system.quotableAccessories.filter(
      (acc) => !allUnitProductIds.has(acc.productId)
    ),
    units: Object.fromEntries(
      system.configuration.condensers.map((condenser) => {
        const unitProductIds = unitScopedProductIds(
          recommendations,
          condenser.uniqueId
        );
        return [
          condenser.uniqueId,
          condenser.selectedAccessories.filter((acc) =>
            unitProductIds.has(acc.productId)
          )
        ];
      })
    )
  };
}

export const selectedAccessoriesFor = (
  selections: DuctedAccessorySelectionsType,
  equipmentUniqueId: string
): AccessoryWithQuantityType[] =>
  equipmentUniqueId === selections.systemUniqueId
    ? selections.system
    : selections.units[equipmentUniqueId] ?? [];

export const withEquipmentAccessories = (
  selections: DuctedAccessorySelectionsType,
  equipmentUniqueId: string,
  accessories: AccessoryWithQuantityType[]
): DuctedAccessorySelectionsType =>
  equipmentUniqueId === selections.systemUniqueId
    ? { ...selections, system: accessories }
    : {
        ...selections,
        units: { ...selections.units, [equipmentUniqueId]: accessories }
      };

export const withSystemAccessories = (
  selections: DuctedAccessorySelectionsType,
  accessories: AccessoryWithQuantityType[]
): DuctedAccessorySelectionsType =>
  withEquipmentAccessories(selections, selections.systemUniqueId, accessories);

export const applyDuctedSelectionChanges = (
  selections: DuctedAccessorySelectionsType,
  changes: DuctedAccessorySelectionChangeType[]
): DuctedAccessorySelectionsType =>
  changes.reduce(
    (current, { equipmentUniqueId, accessory, quantity }) =>
      withEquipmentAccessories(
        current,
        equipmentUniqueId,
        setAccessoryQuantity(
          selectedAccessoriesFor(current, equipmentUniqueId),
          accessory,
          quantity
        )
      ),
    selections
  );

export const flattenDuctedAccessorySelections = (
  selections: DuctedAccessorySelectionsType
): AccessoryWithQuantityType[] =>
  combineProductQuantities([
    ...selections.system,
    ...Object.values(selections.units).flat()
  ]);

export const ductedSelectionEntries = (
  selections: DuctedAccessorySelectionsType
): ManualAccessoryEntryType[] =>
  Object.entries({
    [selections.systemUniqueId]: selections.system,
    ...selections.units
  }).flatMap(([ownerUniqueId, accessories]) =>
    accessories.map((accessory) => ({ ...accessory, ownerUniqueId }))
  );

const hasQuantity = (acc: AccessoryWithQuantityType): boolean =>
  acc.quantity > 0;

export const hasDuctedAccessorySelections = (
  selections: DuctedAccessorySelectionsType
): boolean => flattenDuctedAccessorySelections(selections).some(hasQuantity);

export const toDuctedAccessorySavePayload = (
  selections: DuctedAccessorySelectionsType
): DuctedAccessorySavePayloadItemType[] => [
  ...selections.system.filter(hasQuantity).map((acc) => ({
    productId: acc.productId,
    quantity: acc.quantity,
    productTypeCode: 'accessory' as const
  })),
  ...Object.entries(selections.units).flatMap(([unitUniqueId, accessories]) =>
    accessories.filter(hasQuantity).map((acc) => ({
      productId: acc.productId,
      quantity: acc.quantity,
      productTypeCode: 'condenser_accessory' as const,
      configurationItemUniqueId: unitUniqueId
    }))
  )
];

const collectRecommendedChanges = (
  panels: DuctedAccessoryPanelDataType[],
  include: boolean
): DuctedAccessorySelectionChangeType[] =>
  collectCartableRecommendedAccessories({
    panels,
    includedAccessoryTypes: NO_INCLUDED_ACCESSORY_TYPES
  }).map(({ equipmentUniqueId, accessory, recommendedQuantity }) => ({
    equipmentUniqueId,
    accessory,
    quantity: include ? recommendedQuantity : 0
  }));

const isRecommendedLineSetSelected = (
  selections: DuctedAccessorySelectionsType,
  scope: DuctedLineSetScopeType
): boolean => {
  const recommendedProductId = findRecommendedLineSetProductId(scope.options);
  return (
    recommendedProductId === null ||
    selectedQuantityOf(
      selectedAccessoriesFor(selections, scope.equipmentUniqueId),
      recommendedProductId
    ) >= 1
  );
};

const hasRecommendedLineSet = (scope: DuctedLineSetScopeType): boolean =>
  findRecommendedLineSetProductId(scope.options) !== null;

export function areAllRecommendedDuctedAccessoriesSelected({
  selections,
  panels,
  lineSetScopes
}: DuctedRecommendedSelectionType): boolean {
  const recommended = collectRecommendedChanges(panels, true);
  if (recommended.length === 0 && !lineSetScopes.some(hasRecommendedLineSet)) {
    return false;
  }

  return (
    lineSetScopes.every((scope) =>
      isRecommendedLineSetSelected(selections, scope)
    ) &&
    recommended.every(
      ({ equipmentUniqueId, accessory, quantity }) =>
        selectedQuantityOf(
          selectedAccessoriesFor(selections, equipmentUniqueId),
          accessory.productId
        ) >= quantity
    )
  );
}

const withRecommendedLineSet = (
  selections: DuctedAccessorySelectionsType,
  scope: DuctedLineSetScopeType,
  include: boolean
): DuctedAccessorySelectionsType => {
  const recommendedProductId = findRecommendedLineSetProductId(scope.options);
  if (recommendedProductId === null) return selections;
  const current = selectedAccessoriesFor(selections, scope.equipmentUniqueId);
  return withEquipmentAccessories(
    selections,
    scope.equipmentUniqueId,
    include
      ? replaceLineSet(current, scope.options, recommendedProductId)
      : current.filter((acc) => acc.productId !== recommendedProductId)
  );
};

export function applyIncludeAllRecommended({
  selections,
  panels,
  lineSetScopes,
  include
}: DuctedRecommendedSelectionType & {
  include: boolean;
}): DuctedAccessorySelectionsType {
  const panelChanges = collectRecommendedChanges(panels, include);
  const changes = include
    ? [...panelChanges, ...collectSingleChoiceAlternativesToClear(panels)]
    : panelChanges;
  const next = applyDuctedSelectionChanges(selections, changes);

  return lineSetScopes.reduce(
    (current, scope) => withRecommendedLineSet(current, scope, include),
    next
  );
}

const distributeEvenly = (
  quantity: number,
  equipmentUniqueIds: string[]
): SavedRowTargetType[] => {
  const base = Math.floor(quantity / equipmentUniqueIds.length);
  const remainder = quantity % equipmentUniqueIds.length;
  return equipmentUniqueIds
    .map((equipmentUniqueId, index) => ({
      equipmentUniqueId,
      quantity: base + (index < remainder ? 1 : 0)
    }))
    .filter((target) => target.quantity > 0);
};

const savedRowTargets = (
  row: DuctedSavedAccessoryRowType,
  selections: DuctedAccessorySelectionsType,
  recommendations: QuotableSystemAccessoryRecommendationType[]
): SavedRowTargetType[] => {
  if (row.condenserUniqueId && row.condenserUniqueId in selections.units) {
    return [
      { equipmentUniqueId: row.condenserUniqueId, quantity: row.quantity }
    ];
  }
  const recommendingUnits = Array.from(
    new Set(
      recommendations
        .filter(
          (rec) =>
            isUnitScopedRecommendation(rec) &&
            rec.accessoryProductId === row.productId &&
            rec.uniqueId in selections.units
        )
        .map((rec) => rec.uniqueId)
    )
  );
  if (recommendingUnits.length === 0) {
    return [
      { equipmentUniqueId: selections.systemUniqueId, quantity: row.quantity }
    ];
  }
  return distributeEvenly(row.quantity, recommendingUnits);
};

const addAccessoryQuantity = (
  accessories: AccessoryWithQuantityType[],
  product: AccessoryProductType,
  quantity: number
): AccessoryWithQuantityType[] =>
  setAccessoryQuantity(
    accessories,
    product,
    selectedQuantityOf(accessories, product.productId) + quantity
  );

const poolUnattributedRows = (
  savedRows: DuctedSavedAccessoryRowType[]
): DuctedSavedAccessoryRowType[] => {
  const pooled = new Map<number, DuctedSavedAccessoryRowType>();
  const attributed: DuctedSavedAccessoryRowType[] = [];
  savedRows.forEach((row) => {
    if (row.condenserUniqueId) {
      attributed.push(row);
      return;
    }
    const existing = pooled.get(row.productId);
    pooled.set(
      row.productId,
      existing
        ? { ...existing, quantity: existing.quantity + row.quantity }
        : { ...row }
    );
  });
  return [...attributed, ...pooled.values()];
};

export function hydrateDuctedSelectionsFromSavedRows({
  savedRows,
  recommendations,
  requiredGroups,
  system
}: HydrateSelectionsParams): DuctedAccessorySelectionsType {
  const lookupProduct = buildSavedAccessoryProductLookup(
    recommendations,
    requiredGroups,
    system.quotableAccessories,
    savedRows
  );

  return poolUnattributedRows(savedRows).reduce((selections, row) => {
    const product = lookupProduct(row.productId);
    if (!product) return selections;
    return savedRowTargets(row, selections, recommendations).reduce(
      (current, { equipmentUniqueId, quantity }) =>
        withEquipmentAccessories(
          current,
          equipmentUniqueId,
          addAccessoryQuantity(
            selectedAccessoriesFor(current, equipmentUniqueId),
            product,
            quantity
          )
        ),
      selections
    );
  }, emptyDuctedAccessorySelections(system));
}
